import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  TextField,
} from '@mui/material';
import {
  Login as LoginIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  ExitToApp as ExitToAppIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useHousehold } from '../../contexts/HouseholdContext';
import { useNotifications } from '../../hooks/useNotifications';
import { createHouseholdInvitation, createHousehold, leaveHousehold, deleteMemberFromHousehold, deleteHousehold, updateHousehold } from '../../api/household';
import { translations } from '../../locales/pl';
import { styles } from './Households.styles';
import { useNavigate } from 'react-router-dom';
import InviteUserDrawer from './InviteUserDrawer';
import CreateHouseholdDrawer from './CreateHouseholdDrawer';
import { Household, HouseholdMember } from '../../types/household';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { getCurrentUserId } from '../../utils/auth';

const Households: React.FC = () => {
  const { households, setActiveHousehold, fetchUserHouseholds, isLoading, activeHouseholdId } = useHousehold();
  const { showSuccess, showError } = useNotifications();
  const navigate = useNavigate();
  const [selectedHousehold, setSelectedHousehold] = useState<Household | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{ household: Household; member: HouseholdMember } | null>(null);
  const [confirmLeaveDialogOpen, setConfirmLeaveDialogOpen] = useState(false);
  const [householdToLeave, setHouseholdToLeave] = useState<Household | null>(null);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [householdToDelete, setHouseholdToDelete] = useState<Household | null>(null);
  const [editingHouseholdId, setEditingHouseholdId] = useState<string | null>(null);
  const [editedName, setEditedName] = useState<string>('');

  useEffect(() => {
    fetchUserHouseholds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnterHousehold = (household: Household) => {
    setActiveHousehold(household.id);
    const householdName = household.isSystem && household.name === 'Default'
      ? translations.households.Default
      : household.name;
    showSuccess(`${translations.households.switchedToHousehold}: ${householdName}`);
    navigate('/');
  };

  const handleOpenInviteDrawer = (household: Household) => {
    setSelectedHousehold(household);
    setDrawerOpen(true);
  };

  const handleCloseInviteDrawer = () => {
    setDrawerOpen(false);
    setSelectedHousehold(null);
  };

  const handleInvite = async (email: string) => {
    if (!selectedHousehold) return;

    try {
      await createHouseholdInvitation(selectedHousehold.id, { email });
      showSuccess(translations.households.invitationSent);
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : translations.households.invitationFailed
      );
      throw error;
    }
  };

  const handleRemoveMemberClick = (household: Household, member: HouseholdMember) => {
    setMemberToRemove({ household, member });
    setConfirmDialogOpen(true);
  };

  const handleConfirmRemoveMember = async () => {
    if (!memberToRemove) return;

    try {
      await deleteMemberFromHousehold(memberToRemove.household.id, memberToRemove.member.id);
      showSuccess(translations.households.memberRemoved);
      await fetchUserHouseholds();
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : translations.households.removeFailed
      );
    } finally {
      setConfirmDialogOpen(false);
      setMemberToRemove(null);
    }
  };

  const handleCancelRemoveMember = () => {
    setConfirmDialogOpen(false);
    setMemberToRemove(null);
  };

  const handleLeaveHouseholdClick = (household: Household) => {
    setHouseholdToLeave(household);
    setConfirmLeaveDialogOpen(true);
  };

  const handleConfirmLeaveHousehold = async () => {
    if (!householdToLeave) return;

    try {
      await leaveHousehold(householdToLeave.id);
      showSuccess(translations.households.householdLeft);
      await fetchUserHouseholds();
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : translations.households.leaveFailed
      );
    } finally {
      setConfirmLeaveDialogOpen(false);
      setHouseholdToLeave(null);
    }
  };

  const handleCancelLeaveHousehold = () => {
    setConfirmLeaveDialogOpen(false);
    setHouseholdToLeave(null);
  };

  const handleOpenCreateDrawer = () => {
    setCreateDrawerOpen(true);
  };

  const handleCloseCreateDrawer = () => {
    setCreateDrawerOpen(false);
  };

  const handleCreateHousehold = async (name: string) => {
    try {
      await createHousehold({ name });
      showSuccess(translations.households.householdCreated);
      await fetchUserHouseholds();
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : translations.households.householdCreationFailed
      );
      throw error;
    }
  };

  const handleDeleteHouseholdClick = (household: Household) => {
    setHouseholdToDelete(household);
    setConfirmDeleteDialogOpen(true);
  };

  const handleConfirmDeleteHousehold = async () => {
    if (!householdToDelete) return;

    try {
      await deleteHousehold(householdToDelete.id);
      showSuccess(translations.households.householdDeleted);

      // If deleted household was active, clear it
      if (activeHouseholdId === householdToDelete.id) {
        localStorage.removeItem('activeHouseholdId');
        await fetchUserHouseholds();
        navigate('/households');
      } else {
        await fetchUserHouseholds();
      }
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : translations.households.deleteFailed
      );
    } finally {
      setConfirmDeleteDialogOpen(false);
      setHouseholdToDelete(null);
    }
  };

  const handleCancelDeleteHousehold = () => {
    setConfirmDeleteDialogOpen(false);
    setHouseholdToDelete(null);
  };

  const handleEditNameClick = (household: Household) => {
    setEditingHouseholdId(household.id);
    setEditedName(household.name);
  };

  const handleSaveName = async (householdId: string) => {
    if (!editedName.trim()) {
      showError(translations.households.nameRequired);
      return;
    }

    try {
      await updateHousehold(householdId, { name: editedName });
      showSuccess(translations.households.nameUpdated);
      await fetchUserHouseholds();
      setEditingHouseholdId(null);
      setEditedName('');
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : translations.households.updateFailed
      );
    }
  };

  const handleCancelEdit = () => {
    setEditingHouseholdId(null);
    setEditedName('');
  };

  if (isLoading) {
    return (
      <Box sx={{ ...styles.container, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ marginTop: 2 }}>{translations.common.loading}</Typography>
      </Box>
    );
  }

  if (!households || households.length === 0) {
    return (
      <Box sx={styles.container}>
        <Typography variant="h4" component="h1" sx={styles.header}>
          {translations.households.title}
        </Typography>
        <Box sx={styles.emptyState}>
          <Typography>{translations.households.noHouseholds}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      {households.map((household) => {
        const currentUserId = getCurrentUserId();
        const currentUserMember = household.members?.find((m) => m.id === currentUserId);
        const userIsOwner = currentUserMember?.role === 'owner';

        return (
          <Card key={household.id} sx={styles.householdCard}>
            <CardContent>
              <Box sx={styles.householdHeader}>
                {editingHouseholdId === household.id ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <TextField
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      size="small"
                      fullWidth
                      autoFocus
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleSaveName(household.id)}
                    >
                      {translations.common.save}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleCancelEdit}
                    >
                      {translations.common.cancel}
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">
                      {household.isSystem && household.name === 'Default'
                        ? translations.households.Default
                        : household.name}
                    </Typography>
                    {userIsOwner && !household.isSystem && (
                      <IconButton
                        size="small"
                        onClick={() => handleEditNameClick(household)}
                        sx={{ padding: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                )}
              </Box>

              {household.members && household.members.length > 0 && (
                <Box sx={styles.membersList}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translations.households.members} ({household.members.length})
                  </Typography>
                  <List dense>
                    {household.members.map((member, index) => (
                      <ListItem key={index} sx={styles.memberItem}>
                        <PersonIcon fontSize="small" color="action" />
                        <ListItemText
                          primary={`${member.firstName} ${member.lastName}`}
                        />
                        {userIsOwner && member.role === 'member' && (
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleRemoveMemberClick(household, member)}
                            sx={{ marginRight: 0 }}
                          >
                            {translations.households.removeMember}
                          </Button>
                        )}
                        <Chip
                          label={
                            member.role === 'owner'
                              ? translations.households.owner
                              : translations.households.member
                          }
                          size="small"
                          color={member.role === 'owner' ? 'primary' : 'default'}
                          variant="outlined"
                          sx={styles.ownerChip}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              <Box sx={styles.actionsContainer}>
                {userIsOwner && !household.isSystem && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PersonAddIcon />}
                    onClick={() => handleOpenInviteDrawer(household)}
                    fullWidth
                  >
                    {translations.households.addMember}
                  </Button>
                )}
                {!userIsOwner && !household.isSystem && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<ExitToAppIcon />}
                    onClick={() => handleLeaveHouseholdClick(household)}
                    fullWidth
                  >
                    {translations.households.leave}
                  </Button>
                )}
                {userIsOwner && !household.isSystem && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteHouseholdClick(household)}
                    fullWidth
                  >
                    {translations.households.deleteHousehold}
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<LoginIcon />}
                  onClick={() => handleEnterHousehold(household)}
                  fullWidth
                >
                  {translations.households.enter}
                </Button>
              </Box>
            </CardContent>
          </Card>
        );
      })}

      <InviteUserDrawer
        open={drawerOpen}
        onClose={handleCloseInviteDrawer}
        householdName={
          selectedHousehold
            ? selectedHousehold.isSystem && selectedHousehold.name === 'Default'
              ? translations.households.Default
              : selectedHousehold.name
            : ''
        }
        onInvite={handleInvite}
      />

      <ConfirmDialog
        open={confirmDialogOpen}
        title={translations.households.confirmRemoveMemberTitle}
        message={translations.households.confirmRemoveMemberMessage}
        onConfirm={handleConfirmRemoveMember}
        onCancel={handleCancelRemoveMember}
        confirmText={translations.households.removeMember}
      />

      <ConfirmDialog
        open={confirmLeaveDialogOpen}
        title={translations.households.confirmLeaveTitle}
        message={translations.households.confirmLeaveMessage}
        onConfirm={handleConfirmLeaveHousehold}
        onCancel={handleCancelLeaveHousehold}
        confirmText={translations.households.leave}
      />

      <ConfirmDialog
        open={confirmDeleteDialogOpen}
        title={translations.households.confirmDeleteTitle}
        message={translations.households.confirmDeleteMessage}
        onConfirm={handleConfirmDeleteHousehold}
        onCancel={handleCancelDeleteHousehold}
        confirmText={translations.common.delete}
      />

      <CreateHouseholdDrawer
        open={createDrawerOpen}
        onClose={handleCloseCreateDrawer}
        onCreate={handleCreateHousehold}
      />

      <Box sx={{ marginTop: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleOpenCreateDrawer}
        >
          {translations.households.createHousehold}
        </Button>
      </Box>
    </Box>
  );
};

export default Households;
